import { Check, Sparkles, Monitor, Apple } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useDownloads, isLive } from "@/lib/useDownloads";
import { usePricing, money, savingsLabel } from "@/lib/usePricing";

const plans = [
  {
    id: "trial",
    name: "7-day free trial",
    price: "$0",
    period: "for 7 days",
    tagline: "Try Xpattern on your own computer and plotter before you pay a cent.",
    bullets: [
      "Full Access",
      "Real cuts on your plotter, not a demo",
      "No Card Required",
      "Reminder before the trial ends; upgrade anytime",
    ],
    cta: "Start free trial",
  },
  {
    id: "monthly",
    name: "Monthly",
    period: "/ month",
    tagline: "Pay as you go. Cancel anytime — you're never interrupted.",
    bullets: [
      "Full Access",
      "Window Tint",
      "PPF (Coming Soon)",
      "Unlimited Cuts",
      "New vehicles update automatically into your profile",
      "Precise Cuts, VIN lookup and Calibration",
      "Submit Vehicle Request",
      "Auto-pay on the same day each month, or pay one month at a time",
    ],
    cta: "Subscribe monthly",
    compare: { period: "/ month" },
  },
  {
    id: "yearly",
    name: "Yearly",
    period: "/ year",
    tagline: "One payment covers the whole year — no monthly renewals to think about.",
    bullets: [
      "Everything in Monthly",
      "PPF (Coming Soon)",
      "Latest vehicles loaded automatically all year",
      "Renews once a year on your purchase date",
      "One receipt for the books, zero monthly card hassle",
    ],
    cta: "Get the yearly plan",
    featured: true,
    compare: { period: "/ year" },
  },
];

const DownloadButton = ({ platform, plan, icon: Icon, label, live, cls }) => {
  const testId = platform === "windows" ? `pricing-cta-${plan.id}` : `pricing-mac-${plan.id}`;
  if (isLive(live)) {
    return (
      <a href={live.url} download rel="noopener" className={`btn ${cls} w-full`} data-testid={testId} data-live="true">
        <Icon size={16} /> {label}
      </a>
    );
  }
  return (
    <button type="button" disabled aria-disabled="true" className="btn btn-disabled w-full" data-testid={testId} data-live="false">
      <Icon size={16} /> {label}
    </button>
  );
};

const resolvePlan = (plan, pricing) => {
  const p = pricing[plan.id];
  if (!p) return plan;
  const save = savingsLabel(plan.id, p);
  return { ...plan, price: money(p.price), compare: save ? { ...plan.compare, others: money(p.others), save } : null };
};

const PlanCard = ({ plan, index, downloads }) => (
  <Reveal delay={index * 0.1} className="h-full">
    <div
      className={`card relative flex h-full flex-col p-7 sm:p-8 ${
        plan.featured ? "border-blue/70 shadow-[0_0_0_1px_rgba(47,128,255,0.5),0_0_60px_rgba(47,128,255,0.25)]" : ""
      }`}
      data-testid={`pricing-card-${plan.id}`}
    >
      {plan.featured && (
        <span
          className="absolute -top-3 left-7 inline-flex items-center gap-1.5 rounded-full bg-blue px-3 py-1 text-xs font-semibold text-white shadow-[0_6px_20px_rgba(47,128,255,0.5)]"
          data-testid="pricing-best-value-pill"
        >
          <Sparkles size={12} /> Best value
        </span>
      )}
      <p className="mono-label">{plan.name}</p>
      <div className="mt-4 flex items-baseline gap-2">
        <span className="display text-6xl text-white" data-testid={`pricing-price-${plan.id}`}>
          {plan.price}
        </span>
        <span className="text-sm text-muted">{plan.period}</span>
      </div>
      {plan.compare && (
        <div
          className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3"
          data-testid={`pricing-compare-${plan.id}`}
        >
          <div className="text-sm leading-tight">
            <p className="text-muted">
              Other big software companies charge <span className="font-semibold text-silver line-through decoration-red-400/80 decoration-2">{plan.compare.others}</span>
              <span className="text-muted">{plan.compare.period}</span>
            </p>
          </div>
          <span className="shrink-0 rounded-full bg-emerald-500/20 px-2.5 py-1 text-xs font-semibold text-emerald-300">{plan.compare.save}</span>
        </div>
      )}
      <p className="mt-4 text-[15px] leading-relaxed text-muted">{plan.tagline}</p>
      <ul className="mt-8 flex-1 space-y-3.5">
        {plan.bullets.map((b) => (
          <li key={b} className="flex items-start gap-3 text-[15px] text-body">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue/15 text-blue-text ring-1 ring-blue/30">
              <Check size={12} strokeWidth={3} />
            </span>
            {b}
          </li>
        ))}
      </ul>
      <div className="mt-10 space-y-3">
        <DownloadButton platform="windows" plan={plan} icon={Monitor} label="Download for Windows" live={downloads.plans?.[plan.id] === false ? null : downloads.windows} cls="btn-primary" />
        <DownloadButton platform="mac" plan={plan} icon={Apple} label="Download for Mac iOS" live={downloads.plans?.[plan.id] === false ? null : downloads.mac} cls="btn-mac" />
      </div>
    </div>
  </Reveal>
);

export const PricingCards = () => {
  const downloads = useDownloads();
  const pricing = usePricing();
  return (
    <div className="grid gap-6 lg:grid-cols-3 lg:gap-8" data-testid="pricing-grid">
      {plans.map((p, i) => <PlanCard key={p.id} plan={resolvePlan(p, pricing)} index={i} downloads={downloads} />)}
    </div>
  );
};
